import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InstantPick } from '../components/InstantPick'; // [FLUID-8 2026-10-12]
import styled, { useTheme } from 'styled-components';
import { lgTile } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { fetchDialingReport, peekCache, DialingReport, DialingTeam, DialingMember, DialingTeamGrand, DialingDept } from '../services/api';
import { SlideViewport, SlidePane, slidePos } from '../components/ViewSlide'; // [VIEW-SLIDE 2026-10-08]
import { useAuth } from '../contexts/AuthContext';
import DateRangeCalendar from '../components/DateRangeCalendar';
import { SkeletonBox } from '../components/Skeleton';
import { LoadFailed, isForbidden } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import Tooltip from '../components/Tooltip';
import { pillTrack, pillSegment } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]
import { useRowsLand } from '../components/Fluid'; // [SMOOTH-DATES 2026-10-12] [FLUID-5 2026-10-12]

/**
 * Dialing Report — "Video Meets & Calls", one table per live HubSpot team. A
 * left-side PM/Sales toggle switches which department's teams are shown.
 *
 * The toggle — and which side a user can view — is gated by their HubSpot
 * classification (auth `directory_role`): admins, seniors, and people on BOTH a
 * PM and a Sales team see both sides (toggle shown, defaults to PM); a PM-only
 * person sees only the PM side and a Sales-only person only the Sales side, each
 * with the toggle hidden so they land on — and stay on — their own department.
 *
 * Renders inside AppLayout's shared PageCard, so it adds no outer card/margins.
 * Data comes from GET /api/dialing-report (read-only; never audit-logged). The
 * date filter selects an inclusive RANGE (defaulting to today→today); every
 * metric is summed across the range and the two top KPIs recompute for it.
 *
 * Top KPIs:
 *   • Total Talktime  = Σ everyone's talktime over the range
 *   • Daily Average   = Σ talktime(active) / COUNT(persons with talktime > 0)
 *     (the Looker formula — zero-talktime people are excluded from both halves)
 *
 * Total Talktime cell colour: < 1h30m red, 1h30m–2h orange, > 2h green.
 */

/* ───────────────────────── time + colour helpers ───────────────────────── */

const HOUR_1_5 = 5400; // 1h30m
const HOUR_2 = 7200; // 2h

function fmtHMS(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const x = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(x).padStart(2, '0')}`;
}

function talktimeColor(seconds: number, dark: boolean): string {
  if (seconds < HOUR_1_5) return dark ? '#f87171' : '#dc2626'; // red
  if (seconds <= HOUR_2) return dark ? '#fbbf24' : '#d97706'; // orange
  return dark ? '#4acd8b' : '#1a9f7d'; // green
}

/* ───────────────────────── layout ───────────────────────── */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  /* Bottom padding clears the floating dock so the last team table sits above
     the pill instead of hiding behind it. */
  padding: 4px 2px 88px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
`;

/* Left cluster — the page title with the PM/Sales toggle directly to its right. */
const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex-wrap: wrap;

  /* [DIALING PHONE HEADER 2026-09-23, user] Phones: this row spans the width with the title on
     the LEFT and the PM | Sales pill at the RIGHT edge. On a phone too narrow for both, the pill
     wraps under the title. Users without the toggle just get the title. Desktop unchanged. */
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

/* PM / Sales segmented toggle — one pill split in two equal halves; a green
   "thumb" slides under the active half.
   [BUTTON-STYLE 2026-09-30] Track and segment sizes come from styles/buttons (pillTrack / pillSegment). */
const Segmented = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  ${pillTrack}
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.05)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')};
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border-color: transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
`;

const SegThumb = styled.div<{ $right: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc((100% - 6px) / 2);
  border-radius: 999px;
  background: linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.32)' : 'rgba(20, 107, 84, 0.32)')};
  transform: translateX(${(p) => (p.$right ? '100%' : '0')});
  transition: transform 0.26s cubic-bezier(0.32, 0.72, 0, 1);
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.32)' : 'rgba(20, 107, 84, 0.32)')}, inset 0 1px 0 rgba(255,255,255,0.45);
  transition: transform 0.42s cubic-bezier(0.32, 0.72, 0, 1);
`;

const Seg = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  ${pillSegment}
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#fff' : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => (p.$active ? '#fff' : p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
  }
`;

const TitleIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.theme.greenTint};
  color: ${(p) => p.theme.greenInk}; /* [GREEN-ON-DARK 2026-08-27] */
  font-size: 18px;
  flex-shrink: 0;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 800;
  color: ${(p) => p.theme.text};
  margin: 0;
  letter-spacing: -0.2px;
`;

const Sub = styled.p`
  margin: 3px 0 0;
  font-size: 12.5px;
  color: ${(p) => p.theme.muted};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;

  /* [DIALING PHONE HEADER 2026-09-23, user] Phones: the Total Talktime / Daily Average strip and
     the date picker sit together in the MIDDLE of their row. Each wraps onto its own centred line
     if a very narrow phone cannot fit both. Desktop unchanged. */
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

/* The two KPIs grouped into one soft-glass strip so they read as a deliberate
   module instead of floating text in the header's empty space. */
const StatStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 8px 18px;
  border-radius: 12px;
  background: ${(p) =>
    p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.45) 100%)'};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)')};
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

const StatDivider = styled.div`
  width: 1px;
  align-self: stretch;
  margin: 2px 0;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)')};
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  /* Signals the KPI carries an explanatory hover tooltip. */
  cursor: help;
`;

const StatLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) { font-size: 11px; }
`;

const StatValue = styled.span`
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
  color: ${(p) => p.theme.text};
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.4px;
`;

/* Placeholder shown in the header during the first (skeleton) load so the date
   picker doesn't pop in — and the header doesn't shift — when data lands. It
   mirrors DateRangeCalendar's Trigger frame (same height/border/radius). */
/* The date-range control, sized to match the KPI strip: it stretches to the
   Controls row so it's never shorter than the Total Talktime / Daily Average box
   sitting beside it. */
const DateControl = styled(DateRangeCalendar)`
  && {
    align-self: stretch;
    height: auto;
    min-height: 48px;
    font-size: 14px;
  }
`;

const DatePlaceholder = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  min-height: 48px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.75)')};
  i.cal { color: ${(p) => p.theme.greenInk}; font-size: 13px; } /* [GREEN-ON-DARK 2026-08-27] */
  i.chev { color: ${(p) => p.theme.muted}; font-size: 11px; margin-left: 2px; }
`;

/* ───────────────────────── team table ───────────────────────── */

const TeamSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TeamHeader = styled.div`
  text-align: center;
`;

const TeamHeaderText = styled.span`
  display: inline-block;
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  padding-bottom: 4px;
  border-bottom: 2px solid ${(p) => p.theme.green600};
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1320px;
  border-collapse: collapse;
  /* Fixed layout + the shared <TableCols> colgroup below give every table the
     SAME column widths regardless of its own content, so columns line up
     vertically across all the stacked team tables (and between the skeleton and
     the data that replaces it). Without this each table auto-sizes from its own
     content and the columns drift out of sync. */
  table-layout: fixed;
`;

const Th = styled.th<{ $left?: boolean }>`
  background: ${(p) => p.theme.pill};
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  white-space: nowrap;
  text-align: ${(p) => (p.$left ? 'left' : 'center')};
  border-bottom: 1px solid ${(p) => p.theme.line};

  &:first-child { border-top-left-radius: 13px; }
  &:last-child { border-top-right-radius: 13px; }
`;

const Tr = styled.tr`
  & > td {
    transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  /* Row-hover tint gated by html.pointer-active (toggled in utils/hoverGate.ts)
     + hover:hover so it can never stick on a row after scrolling/reload and so
     touch devices never get a sticky highlight. */
  @media (hover: hover) {
    html.pointer-active &:hover td {
      background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(22,25,23,0.025)')};
    }
  }
`;

const Td = styled.td<{ $left?: boolean }>`
  padding: 9px 12px;
  font-size: 13px;
  color: ${(p) => p.theme.text};
  border-bottom: 1px solid ${(p) => p.theme.line};
  text-align: ${(p) => (p.$left ? 'left' : 'center')};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const NameCell = styled(Td)`
  font-weight: 600;
`;

const TalkTd = styled.td<{ $color: string }>`
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.$color};
  border-bottom: 1px solid ${(p) => p.theme.line};
  text-align: center;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const GrandTr = styled.tr`
  td {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(22,25,23,0.04)')};
    font-weight: 700;
    border-top: 2px solid ${(p) => p.theme.line};
    border-bottom: none;
  }
`;

const Note = styled.div`
  padding: 56px 24px;
  text-align: center;
  color: ${(p) => p.theme.muted};
  font-size: 14px;
`;

/* [VIEW-SLIDE 2026-10-08, user] PM <-> Sales SLIDES like the app's other view toggles (components/ViewSlide): going
   right (PM -> Sales) the PM tables leave to the LEFT while Sales comes in from the RIGHT; back reverses it. Only the
   tables move — the title, the pill, the KPI strip and the date control stay put. Each pane keeps Wrap's 22px rhythm. */
const DEPTS: readonly DialingDept[] = ['PM', 'Sales'];
const PaneStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  /* [SMOOTH-DATES 2026-10-12, user] After a date pick the new rows rise in as they land instead of snapping in.
     [FLUID-5 2026-10-12, user] Every row on screen, top to bottom across the teams, as one wave (the page's
     useRowsLand on this stack — components/Fluid.tsx landRows). */
`;
/** [VIEW-SLIDE 2026-10-08] What one department's pane shows: the live state, or the frozen copy a leaving pane keeps. */
type DeptView = { dept: DialingDept; loading: boolean; error: string | null; forbidden: boolean; report: DialingReport | null };

/* ───────────────────────── skeleton ───────────────────────── */

// Mirrors one real TeamSection — centered title, the actual column-header row,
// a few body rows and the grand-total row — using the SAME table components, so
// the loading state lines up exactly with the data that replaces it (no layout
// jump) instead of a flat block of bars. COLS is referenced at render time, so
// its later declaration is fine.

// Shared <colgroup>: Name + the 8 metric columns + Total Talktime + Daily Avg,
// widths summing to 100%. Rendered by BOTH the skeleton and the real table so,
// together with table-layout:fixed, every table sizes its columns identically
// and they stay vertically aligned across teams. Like SkTeam, this reads COLS
// at render time, so its later declaration is fine.
const TableCols: React.FC = () => (
  <colgroup>
    <col style={{ width: '14%' }} />
    {COLS.map((c) => (
      <col key={c.key as string} style={{ width: '8.125%' }} />
    ))}
    <col style={{ width: '12%' }} />
    <col style={{ width: '9%' }} />
  </colgroup>
);

// [SMOOTH-DATES 2026-10-12, user] "When I select the dates it jumps": the bars were blocks 13-14px tall, while a
// line of text is ~16-18px, so every skeleton row and team title was a few px SHORTER than the real one and the page
// grew when the numbers landed. The bars now sit inline on the cell's own text line (inline-block, middle) — a
// skeleton row is exactly as tall as a row of numbers — and the grand-total cells carry the same 13px font.
const inlineBar: React.CSSProperties = { display: 'inline-block', verticalAlign: 'middle' };
const SkBar: React.FC<{ w: string; h?: string }> = ({ w, h = '13px' }) => <SkeletonBox as="span" $w={w} $h={h} style={inlineBar} />;

const SkTeam: React.FC<{ rows: number }> = ({ rows }) => (
  <TeamSection>
    <TeamHeader>
      <TeamHeaderText>
        <SkBar w="150px" h="14px" />
      </TeamHeaderText>
    </TeamHeader>
    <TableWrap>
      <Table>
        <TableCols />
        <thead>
          <tr>
            <Th $left>Name</Th>
            {COLS.map((c) => <Th key={c.key as string}>{c.label}</Th>)}
            <Th>Total Talktime</Th>
            <Th>Daily Avg</Th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <Tr key={r}>
              <NameCell $left><SkBar w="70%" /></NameCell>
              {COLS.map((c) => (
                <Td key={c.key as string}><SkBar w="42%" /></Td>
              ))}
              <Td><SkBar w="64px" /></Td>
              <Td><SkBar w="64px" /></Td>
            </Tr>
          ))}
          <GrandTr>
            <td style={{ textAlign: 'left', padding: '10px 12px', fontSize: 13 }}>
              <SkBar w="55%" />
            </td>
            {COLS.map((c) => (
              <td key={c.key as string} style={{ textAlign: 'center', padding: '10px 12px', fontSize: 13 }}>
                <SkBar w="42%" />
              </td>
            ))}
            <td style={{ textAlign: 'center', padding: '10px 12px', fontSize: 13 }}>
              <SkBar w="64px" />
            </td>
            <td style={{ textAlign: 'center', padding: '10px 12px', fontSize: 13 }}>
              <SkBar w="64px" />
            </td>
          </GrandTr>
        </tbody>
      </Table>
    </TableWrap>
  </TeamSection>
);

/* ───────────────────────── columns ───────────────────────── */

const COLS: { key: keyof DialingMember | 'talk'; label: string }[] = [
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'happened', label: 'Happened' },
  { key: 'noShow', label: 'No Show' },
  { key: 'abandoned', label: 'Abandoned' },
  { key: 'rescheduled', label: 'Rescheduled' },
  { key: 'spillover', label: 'Spillover' },
  { key: 'impulsive', label: 'Impulsive' },
  { key: 'totalCalls', label: 'Total Calls' },
];

/* ───────────────────────── component ───────────────────────── */

const TeamTable: React.FC<{ team: DialingTeam; dark: boolean }> = ({ team, dark }) => {
  const g: DialingTeamGrand = team.grand;
  return (
    <TeamSection>
      <TeamHeader>
        <TeamHeaderText>{team.label}</TeamHeaderText>
      </TeamHeader>
      <TableWrap>
        <Table>
          <TableCols />
          <thead>
            <tr>
              <Th $left>Name</Th>
              {COLS.map((c) => <Th key={c.key as string}>{c.label}</Th>)}
              <Th>Total Talktime</Th>
              <Th>Daily Avg</Th>
            </tr>
          </thead>
          <tbody>
            {team.members.map((m) => (
              <Tr key={m.name}>
                <NameCell $left>{m.name}</NameCell>
                {COLS.map((c) => <Td key={c.key as string}>{m[c.key as keyof DialingMember] as number}</Td>)}
                <TalkTd $color={talktimeColor(m.talktimeSeconds, dark)}>{fmtHMS(m.talktimeSeconds)}</TalkTd>
                <Td>{fmtHMS(m.dailyAvgSeconds)}</Td>
              </Tr>
            ))}
            <GrandTr>
              <td style={{ textAlign: 'left', padding: '10px 12px', fontSize: 13 }}>Grand total</td>
              {COLS.map((c) => (
                <td key={c.key as string} style={{ textAlign: 'center', padding: '10px 12px', fontSize: 13 }}>
                  {g[c.key as keyof DialingTeamGrand]}
                </td>
              ))}
              <td style={{ textAlign: 'center', padding: '10px 12px', fontSize: 13 }}>{fmtHMS(g.talktimeSeconds)}</td>
              <td style={{ textAlign: 'center', padding: '10px 12px', fontSize: 13 }}>{fmtHMS(g.dailyAvgSeconds)}</td>
            </GrandTr>
          </tbody>
        </Table>
      </TableWrap>
    </TeamSection>
  );
};

const todayKey = (): string => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const DialingReportPage: React.FC = () => {
  // Department gating from the signed-in user's HubSpot classification. `user` is
  // always present here (App only renders routed pages when authenticated), so
  // this is stable for the session and safe to seed initial state from.
  const { user } = useAuth();
  // Admins, seniors, and people on BOTH a PM and a Sales team may view both
  // departments (the PM/Sales toggle is shown). Everyone else is pinned to their
  // own side: Sales-only → Sales, PM-only / unclassified → PM.
  const canSeeBoth = !!user && (user.is_admin || user.is_senior || user.directory_role === 'both');
  const defaultDept: DialingDept = !canSeeBoth && user?.directory_role === 'sales' ? 'Sales' : 'PM';

  const [report, setReport] = useState<DialingReport | null>(null);
  const [loading, setLoading] = useState(true); // any load (mount, dept switch, date change) → skeleton
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false); // [LOAD-STATES 2026-09-29] a 403 is final, no countdown
  // Inclusive date range; defaults to today→today (a single day). Every metric is
  // summed across the range server-side.
  const [start, setStart] = useState<string>(todayKey);
  const [end, setEnd] = useState<string>(todayKey);
  // Active department — the toggle right of the title (only for canSeeBoth users).
  // Seeded from the user's allowed default so single-department users land on their
  // own side. Each dept loads its own teams + dates from the same source table.
  const [dept, setDept] = useState<DialingDept>(defaultDept);

  const theme = useTheme() as { colorScheme?: string };
  const dark = theme?.colorScheme === 'dark';
  const today = todayKey();
  // [VIEW-SLIDE 2026-10-08] The pane sliding OUT keeps showing what it had on screen (frozen at the switch).
  const [snap, setSnap] = useState<DeptView | null>(null);
  // [VIEW-SLIDE 2026-10-08] Tags each load so a slower earlier answer (e.g. PM -> Sales -> PM quickly) can never land
  // on top of a newer one and put one department's tables under the other's pill.
  const reqIdRef = useRef(0);
  // [SMOOTH-DATES 2026-10-12] Counts the loads whose rows rise in as they land. [FLUID-5 2026-10-12, user] Each count
  // runs the row wave on the department on show: every row on screen, top to bottom (components/Fluid.tsx).
  const [landing, setLanding] = useState(0);
  const landRef = useRef<HTMLDivElement>(null);
  useRowsLand(landRef, landing > 0 ? landing : null);

  // Every load (initial mount, dept switch, date change) shows the skeleton — so the
  // tables and header KPIs read as "loading" until the fresh data lands, never
  // lingering on the previous result. ([SMOOTH-DATES 2026-10-12] A date change keeps
  // the old report in state, unseen, only to give the skeleton its shape.)
  // [VIEW-SLIDE 2026-10-08] `seeded` = a dept switch already put this exact report on screen from the app's GET cache,
  // so no skeleton; the request itself is the same one as before.
  // [SMOOTH-DATES 2026-10-12] `land` = a date pick: the rows rise in when this answer lands.
  const load = useCallback(async (s: string, e: string, dpt: DialingDept, seeded = false, land = false) => {
    const reqId = ++reqIdRef.current;
    setLoading(!seeded);
    setError(null);
    try {
      const data = await fetchDialingReport(s, e, dpt);
      if (reqId !== reqIdRef.current) return;
      setReport(data);
      setStart(data.start);
      setEnd(data.end);
      if (land) setLanding((n) => n + 1);
    } catch (err: any) {
      if (reqId !== reqIdRef.current) return;
      if (land) setReport(null); // [SMOOTH-DATES 2026-10-12] as before: a failed date pick shows no old figures
      setError(err?.response?.data?.error || 'Failed to load the dialing report.');
      setForbidden(isForbidden(err)); // [LOAD-STATES 2026-09-29]
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = todayKey();
    load(t, t, defaultDept, false, true); // [FLUID-5 2026-10-12] the rows rise in on a tab visit too
  }, [load, defaultDept]);

  // Pick a date (single or range) — the skeleton shows while the new range loads (user's rule 2026-07-20: the
  // skeleton for the new dates, never the previous range's figures lingering or dimmed).
  // [SMOOTH-DATES 2026-10-12, user] …without the jump: the previous report is KEPT (not shown — every part checks
  // `loading` first) so the skeleton takes its exact shape (same teams, same number of rows) and the date button
  // stays where it is, showing the new dates. The new rows then rise in as they land.
  const onPickRange = (s: string, e: string) => {
    if (s === start && e === end) return;
    setStart(s);
    setEnd(e);
    load(s, e, dept, false, true);
  };

  // Switch PM↔Sales — keep the selected range so it's a like-for-like comparison;
  // full-swap the tables (clear stale rows → skeleton, never the other
  // department's tables). The top KPIs + date control then update for the new dept.
  const onPickDept = (d: DialingDept) => {
    if (d === dept) return;
    // [VIEW-SLIDE 2026-10-08, user] The leaving pane keeps what was on screen while it slides out. And no blink: when
    // the app's GET cache already holds this department's report for these dates, it shows at once (no skeleton) —
    // the load below is the same request as before and refreshes it.
    setSnap({ dept, loading, error, forbidden, report });
    const seed = peekCache<DialingReport>('/dialing-report', { start, end, dept: d });
    setDept(d);
    setReport(seed ?? null);
    // [FLUID-5 2026-10-12] From the cache: the arriving pane's rows rise in with the slide (ViewSlide). After a skeleton:
    // they rise in when they land.
    load(start, end, d, !!seed, !seed);
  };

  const totals = report?.totals;

  // [VIEW-SLIDE 2026-10-08] One department's tables (or skeleton / message), as rendered before. `live` = the pane on
  // show; a leaving pane never mounts the retry card — its countdown would fetch for a pane nobody sees.
  const deptBody = (v: DeptView, live: boolean) => {
    const hasPeople = !!v.report && v.report.teams.some((t) => t.members.length > 0);
    // [SMOOTH-DATES 2026-10-12] A date pick keeps the report it replaces: the skeleton copies its teams and row
    // counts, so it is exactly as tall as the tables were (no jump). First load / department switch: the 3-6-4 guess.
    return v.loading ? (
      hasPeople ? (
        v.report!.teams.map((team) => <SkTeam key={team.key} rows={team.members.length} />)
      ) : (
        <>
          <SkTeam rows={3} />
          <SkTeam rows={6} />
          <SkTeam rows={4} />
        </>
      )
    ) : v.error ? (
      // [LOAD-STATES 2026-09-29, user] Was the raw server text ("Internal server error").
      v.forbidden ? <Note>{v.error}</Note> : live ? <LoadFailed what="the dialing report" onRetry={() => load(start, end, dept, false, true) /* [FLUID-6 2026-10-12] the rows rise when a retry lands */} /> : null
    ) : !hasPeople ? (
      <Note>No {v.dept} dialing data for the selected dates.</Note>
    ) : (
      v.report!.teams.map((team) => <TeamTable key={team.key} team={team} dark={dark} />)
    );
  };

  return (
    <Wrap>
      <TopBar>
        <LeftGroup>
          <TitleBlock>
            <TitleIcon>
              <i className="fas fa-phone-volume" />
            </TitleIcon>
            <div>
              <Title>Dialing Report</Title>
              <Sub>Video Meets &amp; Calls</Sub>
            </div>
          </TitleBlock>

          {/* PM/Sales toggle — switches the whole report (tables + KPIs) between
              the two departments; the selected date range is kept. Shown only to
              users allowed both sides (admins/seniors/both); PM-only and Sales-only
              users are pinned to their own department and never see the toggle. */}
          {canSeeBoth && (
            // [FLUID-8 2026-10-12, user] InstantPick: the pill moves on the mouse PRESS; the tables' switch follows.
            <InstantPick value={dept} onPick={onPickDept}>
              {(shown, press) => (
                <Segmented role="group" aria-label="Department">
                  <SegThumb $right={shown === 'Sales'} aria-hidden="true" />
                  <Seg type="button" $active={shown === 'PM'} aria-pressed={shown === 'PM'} {...press('PM')}>
                    PM
                  </Seg>
                  <Seg type="button" $active={shown === 'Sales'} aria-pressed={shown === 'Sales'} {...press('Sales')}>
                    Sales
                  </Seg>
                </Segmented>
              )}
            </InstantPick>
          )}
        </LeftGroup>

        <Controls>
          <StatStrip>
            <Tooltip label="Total talktime over the selected dates" placement="bottom">
              <Stat>
                <StatLabel>Total Talktime</StatLabel>
                <StatValue>
                  {/* [SMOOTH-DATES 2026-10-12] `loading` first: during a date pick the old report is kept for the
                      skeleton's shape, and its totals must not show as if they were the new range's. */}
                  {loading ? (
                    <SkeletonBox as="span" $w="84px" $h="18px" $radius="6px" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                  ) : totals ? (
                    fmtHMS(totals.totalTalktimeSeconds)
                  ) : (
                    '—'
                  )}
                </StatValue>
              </Stat>
            </Tooltip>
            <StatDivider />
            <Tooltip label="Average talktime per active person over the selected dates" placement="bottom">
              <Stat>
                <StatLabel>Daily Average</StatLabel>
                <StatValue>
                  {loading ? (
                    <SkeletonBox as="span" $w="84px" $h="18px" $radius="6px" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                  ) : totals ? (
                    fmtHMS(totals.dailyAverageSeconds)
                  ) : (
                    '—'
                  )}
                </StatValue>
              </Stat>
            </Tooltip>
          </StatStrip>
          {report ? (
            <DateControl
              liquid
              start={start}
              end={end}
              min={report.minDate}
              max={today}
              onChange={onPickRange}
            />
          ) : loading ? (
            // Keep the date control's footprint during the skeleton load so it
            // doesn't pop in (and the header doesn't shift) once data arrives.
            <DatePlaceholder aria-hidden="true">
              <i className="cal fas fa-calendar-day" />
              <SkeletonBox $w="92px" $h="14px" $radius="6px" />
              <i className="chev fas fa-chevron-down" />
            </DatePlaceholder>
          ) : null}
        </Controls>
      </TopBar>

      {/* [VIEW-SLIDE 2026-10-08, user] Both department panes stay mounted and slide; PM rests on the LEFT, Sales on
          the RIGHT (their sides of the pill). A department never shown yet has an empty pane — nothing extra loads. */}
      <SlideViewport>
        {DEPTS.map((d) => (
          <SlidePane key={d} $pos={slidePos(dept, d, DEPTS)} aria-hidden={dept !== d} data-dialing-pane={d}>
            <PaneStack ref={d === dept ? landRef : undefined}>
              {d === dept
                ? deptBody({ dept, loading, error, forbidden, report }, true)
                : snap?.dept === d ? deptBody(snap, false) : null}
            </PaneStack>
          </SlidePane>
        ))}
      </SlideViewport>
    </Wrap>
  );
};

export default DialingReportPage;
